import axios from 'axios';

export const DAON_BILLING_URL = 'https://platform.claude.com/settings/billing';

export interface DaonPolicy {
  monthlyBudgetUsd: string;
  safetyUsd: string;
  saleLimit: number | null;
  revision: number;
}
export interface DaonBalance {
  userId: string;
  applicationId: string;
  balance: number;
  held: number;
  available: number;
  nextExpiresAt: string | null;
  nextExpiring: number;
}
export interface DaonDailyUsage {
  date: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
  costUsd: string | null;
}
export interface DaonUsage {
  organizationId: string;
  periodStart: string;
  reportedThrough: string;
  observedAt: string;
  officialCostReportedThrough: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
  officialCostUsd: string;
  daily: DaonDailyUsage[];
}
export interface DaonOverview {
  applicationId: string;
  policy: DaonPolicy;
  sales: { status: string; remainingBeads: number; updatedAt: string | null; reason: string };
  credits: { issued: number; consumed: number; revoked: number; outstanding: number; pending: number; reserved: number; held: number };
  usage: DaonUsage | null;
  budgetCostUsd: string | null;
  billingUrl: string;
}
export interface DaonAdjustmentInput {
  operationId: string;
  delta: number;
  reason: string;
  expectedBalance: number;
  expectedHeld: number;
  expectedPolicyRevision: number;
}
export interface DaonAdjustment {
  operationId: string;
  userId: string;
  delta: number;
  reason: string;
  actor: string;
  balanceBefore: number;
  balanceAfter: number;
  createdAt: string;
}
export interface DaonAdjustmentPreview {
  operationId: string;
  userId: string;
  delta: number;
  reason: string;
  before: DaonBalance;
  after: DaonBalance;
  remainingBeadsBefore: number;
  remainingBeadsAfter: number;
  policyRevision: number;
  allowed: boolean;
  reasonCode: string;
}
export interface DaonInsider {
  email: string;
  addedBy: string | null;
  createdAt: string | null;
}

export class DaonAdminError extends Error {
  constructor(message: string, public readonly status = 0, public readonly code = '') {
    super(message);
    this.name = 'DaonAdminError';
  }
}

export function invalidDaonResponse(): never { throw new DaonAdminError('다온 운영 정보의 응답 형식이 올바르지 않습니다. 새로고침 후 다시 확인하세요.'); }
export function daonResponseRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalidDaonResponse();
  return value as Record<string, unknown>;
}
function text(value: unknown): string { return typeof value === 'string' ? value : invalidDaonResponse(); }
function nullableText(value: unknown): string | null { return value === null ? null : text(value); }
function count(value: unknown): number { return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : invalidDaonResponse(); }
function signedCount(value: unknown): number { return typeof value === 'number' && Number.isSafeInteger(value) ? value : invalidDaonResponse(); }
function money(value: unknown): string { return typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value) && Number.isFinite(Number(value)) ? value : invalidDaonResponse(); }
function tokens(row: Record<string, unknown>) {
  return { inputTokens: count(row['inputTokens']), outputTokens: count(row['outputTokens']), cacheReadInputTokens: count(row['cacheReadInputTokens']), cacheCreationInputTokens: count(row['cacheCreationInputTokens']) };
}
export function parseDaonPolicy(value: unknown): DaonPolicy {
  const row = daonResponseRecord(value);
  return { monthlyBudgetUsd: money(row['monthlyBudgetUsd']), safetyUsd: money(row['safetyUsd']), saleLimit: row['saleLimit'] === null ? null : count(row['saleLimit']), revision: count(row['revision']) };
}
export function parseDaonBalance(value: unknown): DaonBalance {
  const row = daonResponseRecord(value);
  const balance = { userId: text(row['userId']), applicationId: text(row['applicationId']), balance: signedCount(row['balance']), held: count(row['held']), available: count(row['available']), nextExpiresAt: nullableText(row['nextExpiresAt']), nextExpiring: count(row['nextExpiring']) };
  if (balance.applicationId !== 'saju-ai' || balance.available !== Math.max(0, balance.balance - balance.held)) return invalidDaonResponse();
  return balance;
}
export function parseDaonOverview(value: unknown): DaonOverview {
  const row = daonResponseRecord(value);
  if (row['applicationId'] !== 'saju-ai') return invalidDaonResponse();
  const sales = daonResponseRecord(row['sales']);
  const credits = daonResponseRecord(row['credits']);
  const usageRow = row['usage'] === null ? null : daonResponseRecord(row['usage']);
  let usage: DaonUsage | null = null;
  if (usageRow) {
    if (!Array.isArray(usageRow['daily'])) return invalidDaonResponse();
    usage = {
      ...tokens(usageRow), organizationId: text(usageRow['organizationId']), periodStart: text(usageRow['periodStart']), reportedThrough: text(usageRow['reportedThrough']), observedAt: text(usageRow['observedAt']), officialCostReportedThrough: nullableText(usageRow['officialCostReportedThrough']), officialCostUsd: money(usageRow['officialCostUsd']),
      daily: usageRow['daily'].map((value) => {
        const day = daonResponseRecord(value);
        return { ...tokens(day), date: text(day['date']), costUsd: day['costUsd'] === null ? null : money(day['costUsd']) };
      }),
    };
  }
  if (row['billingUrl'] !== DAON_BILLING_URL) return invalidDaonResponse();
  return {
    applicationId: 'saju-ai', policy: parseDaonPolicy(row['policy']),
    sales: { status: text(sales['status']), remainingBeads: count(sales['remainingBeads']), updatedAt: nullableText(sales['updatedAt']), reason: text(sales['reason']) },
    credits: { issued: count(credits['issued']), consumed: count(credits['consumed']), revoked: count(credits['revoked']), outstanding: count(credits['outstanding']), pending: count(credits['pending']), reserved: count(credits['reserved']), held: count(credits['held']) },
    usage, budgetCostUsd: row['budgetCostUsd'] === null ? null : money(row['budgetCostUsd']), billingUrl: DAON_BILLING_URL,
  };
}
export function parseDaonAdjustment(value: unknown): DaonAdjustment {
  const row = daonResponseRecord(value);
  const delta = row['delta'];
  if (typeof delta !== 'number' || !Number.isSafeInteger(delta) || delta === 0) return invalidDaonResponse();
  return { operationId: text(row['operationId']), userId: text(row['userId']), delta, reason: text(row['reason']), actor: text(row['actor']), balanceBefore: signedCount(row['balanceBefore']), balanceAfter: signedCount(row['balanceAfter']), createdAt: text(row['createdAt']) };
}
export function parseDaonPreview(value: unknown): DaonAdjustmentPreview {
  const row = daonResponseRecord(value);
  if (typeof row['allowed'] !== 'boolean' || typeof row['delta'] !== 'number' || !Number.isSafeInteger(row['delta']) || row['delta'] === 0) return invalidDaonResponse();
  return { operationId: text(row['operationId']), userId: text(row['userId']), delta: row['delta'], reason: text(row['reason']), before: parseDaonBalance(row['before']), after: parseDaonBalance(row['after']), remainingBeadsBefore: count(row['remainingBeadsBefore']), remainingBeadsAfter: count(row['remainingBeadsAfter']), policyRevision: count(row['policyRevision']), allowed: row['allowed'], reasonCode: text(row['reasonCode']) };
}
export function parseDaonInsiders(value: unknown): DaonInsider[] {
  const row = daonResponseRecord(value);
  if (!Array.isArray(row['testers'])) return invalidDaonResponse();
  return row['testers'].map((entry) => {
    const item = daonResponseRecord(entry);
    return {
      email: text(item['email']),
      addedBy: item['added_by'] === undefined ? null : nullableText(item['added_by']),
      createdAt: item['created_at'] === undefined ? null : nullableText(item['created_at']),
    };
  });
}
export function validateDaonPolicy(monthly: string, safety: string, saleLimit: string, reason: string): string | null {
  const decimal = /^\d+(?:\.\d{1,2})?$/;
  if (!decimal.test(monthly) || !Number.isFinite(Number(monthly)) || Number(monthly) <= 0) return '월 예산은 0보다 큰 USD 금액을 소수점 두 자리까지 입력하세요.';
  if (!decimal.test(safety) || !Number.isFinite(Number(safety)) || Number(safety) >= Number(monthly)) return '안전 준비금은 월 예산보다 작은 USD 금액으로 입력하세요.';
  if (saleLimit !== '' && (!/^\d+$/.test(saleLimit) || !Number.isSafeInteger(Number(saleLimit)))) return '판매 상한은 0 이상의 정수로 입력하거나 비워 두세요.';
  if (!reason.trim() || reason.trim().length > 500) return '변경 사유를 1~500자로 입력하세요.';
  return null;
}
export function validateDaonAdjustment(amount: string, reason: string): string | null {
  if (!/^[1-9]\d*$/.test(amount) || !Number.isSafeInteger(Number(amount)) || Number(amount) > 1_000_000) return '닢 수량은 1~1,000,000 사이 정수로 입력하세요.';
  if (!reason.trim() || reason.trim().length > 500) return '지급·회수 사유를 1~500자로 입력하세요.';
  return null;
}
export function describeDaonError(error: unknown): DaonAdminError {
  if (error instanceof DaonAdminError) return error;
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : '';
    if (status === 409) return new DaonAdminError('잔액 또는 운영 정책이 변경되었습니다. 최신 정보를 불러온 뒤 미리보기를 다시 확인하세요.', status, code);
    if (status === 401) return new DaonAdminError('로그인이 만료되었습니다. 다시 로그인하세요.', status, code);
    if (status === 403) return new DaonAdminError('다온 사용량과 닢을 관리할 권한이 없습니다.', status, code);
    const message = body && typeof body === 'object' && typeof body.message === 'string' ? body.message : status ? '요청을 처리하지 못했습니다.' : '서버에 연결하지 못했습니다. 다시 시도하세요.';
    return new DaonAdminError(message, status, code);
  }
  return new DaonAdminError(error instanceof Error ? error.message : '요청을 처리하지 못했습니다.');
}
